/**
 * La lecture d'un bon d'intervention : en-tête, puis les rubriques DU MODÈLE DE FEUILLE de sa nature (contrôles, valeurs
 * relevées, articles, palier, observations, suites, et selon le modèle : problème signalé, travaux réalisés, réserves du
 * client, dossier technique, période de location).
 *
 * Pure lecture : les gestes (modifier, soumettre, rouvrir, annuler) sont dans l'en-tête de la page, pas ici.
 * Le contenu d'un brouillon vient du bon lui-même ; les libellés des compteurs et le code du palier se
 * relisent sur la machine (un compteur archivé depuis, ou une gamme modifiée, ne fait pas planter l'écran :
 * on dit alors « compteur archivé » ou « palier inconnu » plutôt que d'afficher un identifiant). Une livraison faite sur
 * un site seul n'a pas de machine : rien n'est alors lu dessus.
 *
 * Aucun prix ni total : un bon n'en porte pas.
 */
import { CartePointages } from './CartePointages';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  api,
  type BonDetail,
  type BonValide,
  type ClotureBon,
  type Compteur,
  type ContenuBon,
  type ControleBon,
  type GenreLigneBon,
  type MaintenanceMachine,
} from '../api/client';
import { useDroitsBons, useDroitsPlanning } from '../auth/useDroits';
import { estEnPlus, paliersDeLaGamme, valeurAffichee } from '../domaine/bon-edition';
import { quartDHeure } from '../domaine/bon-heures';
import { dureeFr } from '../domaine/planning';
import { phraseCloture } from '../domaine/validation-bon';
import { ChiffrageBon } from './ChiffrageBon';
import {
  LIBELLE_DOSSIER,
  LIBELLE_FACTURATION,
  classeFacturation,
  configDe,
  libelleTechniciens,
  type ConfigModele,
} from '../domaine/bon-modeles';
import {
  LIBELLE_ETAT_SUITE,
  LIBELLE_GENRE_LIGNE,
  LIBELLE_SUITE,
  jourFr,
  libelleMachine,
  mentionPraxedo,
} from '../domaine/interventions';
import { PiecesJointes } from './PiecesJointes';

function Propriete({ libelle, children }: { libelle: string; children: ReactNode }) {
  const vide = children === null || children === undefined || children === '';
  return (
    <>
      <dt>{libelle}</dt>
      <dd>{vide ? <span className="aide">—</span> : children}</dd>
    </>
  );
}

/**
 * Les contrôles de la nature du bon, tels qu'ils étaient à la saisie : ceux qui sont faits d'abord dans la phrase, puis la liste
 * entière (un contrôle NON fait se voit autant qu'un contrôle fait : le papier montre les deux). Rien si le bon n'en porte pas.
 */
function ControlesDuBon({ controles, titre }: { controles: readonly ControleBon[]; titre: string }) {
  if (controles.length === 0) return null;
  const faits = controles.filter((c) => c.checked).length;
  return (
    <div className="carte">
      <h2>{titre}</h2>
      <div className="carte-corps">
        <p className="aide">
          {faits} sur {controles.length} fait{faits > 1 ? 's' : ''}.
        </p>
        <ul className="controles-bon controles-bon-lecture">
          {controles.map((c) => (
            <li key={c.checkKey} className={c.checked ? 'controle-fait' : 'controle-non-fait'}>
              <span aria-hidden="true" className="controle-marque">
                {c.checked ? '✓' : '○'}
              </span>
              <span className="sr-only">{c.checked ? 'Fait : ' : 'Non fait : '}</span>
              {c.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Un texte libre du bon (problème signalé, travaux, réserves) : une carte, ou rien quand il est vide et que le modèle ne l'exige pas. */
function CarteTexte({ titre, texte, exige }: { titre: string; texte: string; exige?: boolean }) {
  if (texte.trim() === '' && !exige) return null;
  return (
    <div className="carte">
      <h2>{titre}</h2>
      <div className="carte-corps observations-bon">{texte.trim() === '' ? <span className="aide">Non renseigné.</span> : texte}</div>
    </div>
  );
}

/** Une ligne de bon, brouillon ou validée : de quoi la montrer dans un même tableau. */
interface LigneALire {
  readonly cle: string;
  readonly kind: GenreLigneBon;
  readonly reference: string | null;
  readonly designation: string;
  readonly quantity: string | null;
  readonly unit: string | null;
  readonly serialNumber: string | null;
  /** Un brouillon peut être incomplet : on le dit (« non saisie »), alors qu'un bon validé ne l'est plus. */
  readonly brouillon: boolean;
  /** « gamme B » ou « en plus » (lu sur le brouillon) ; absent sur un bon validé (le chiffrage dit compris ou à facturer). */
  readonly marque?: { readonly texte: string; readonly classe: 'pastille-gamme' | 'pastille-en-plus' };
}

function TableauLignes({ lignes, config }: { lignes: readonly LigneALire[]; config: ConfigModele }) {
  const colonneSerie = config.serie === 'COLONNE';
  return (
    <div className="tableau-defilant">
      <table className="tableau-bon">
        <thead>
          <tr>
            {config.colonneType && <th>Type</th>}
            {config.colonneReference && <th>Référence</th>}
            <th>Désignation</th>
            {colonneSerie && <th>N° de série</th>}
            <th>Quantité</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.cle}>
              {config.colonneType && <td>{LIBELLE_GENRE_LIGNE[l.kind]}</td>}
              {config.colonneReference && <td>{l.reference ?? <span className="aide">—</span>}</td>}
              <td>
                {l.designation || (l.brouillon ? <span className="aide">sans désignation</span> : <span className="aide">—</span>)}
                {config.serie === 'DANS_DESIGNATION' && l.serialNumber ? <span className="aide"> — n° de série {l.serialNumber}</span> : null}
                {l.marque && <span className={l.marque.classe}> {l.marque.texte}</span>}
              </td>
              {colonneSerie && <td>{l.serialNumber ?? <span className="aide">—</span>}</td>}
              <td>
                {l.quantity === null ? <span className="aide">{l.brouillon ? 'non saisie' : '—'}</span> : valeurAffichee(l.quantity)}
                {l.unit ? ` ${l.unit}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CarteLignes({ lignes, config }: { lignes: readonly LigneALire[]; config: ConfigModele }) {
  return (
    <div className="carte">
      <h2>{config.titreLignes}</h2>
      <div className="carte-corps">
        {lignes.length === 0 ? (
          <p className="aide">
            {config.genres.includes('ACCESSORY') ? 'Aucun accessoire dans ce bon.' : config.genres.includes('EQUIPMENT') && !config.genres.includes('LABOR') ? 'Aucun matériel dans ce bon.' : 'Aucun article dans ce bon.'}
          </p>
        ) : (
          <TableauLignes lignes={lignes} config={config} />
        )}
      </div>
    </div>
  );
}

/** La période d'une location, et le dossier d'une installation : une phrase chacune, sous le titre de leur rubrique. */
function CarteDossier({ dossier }: { dossier: BonDetail['details']['dossier'] }) {
  return (
    <div className="carte">
      <h2>Dossier technique et certificats</h2>
      <div className="carte-corps">
        {dossier === null ? <span className="aide">Non précisé.</span> : <p>Dossier technique et certificats {LIBELLE_DOSSIER[dossier]}.</p>}
      </div>
    </div>
  );
}

function CartePeriode({ du, au }: { du: string | null; au: string | null }) {
  return (
    <div className="carte">
      <h2>Période de location</h2>
      <div className="carte-corps">
        <p>
          Mise à disposition le <strong>{du ? jourFr(du) : '—'}</strong> — retour prévu le <strong>{au ? jourFr(au) : '—'}</strong>.
        </p>
      </div>
    </div>
  );
}

/**
 * Ce que la validation a écrit : relevés (avec leur éventuel signalement), entretien, articles, suites. Le brouillon est
 * vidé à la validation : tout se relit dans des lignes que rien ne modifie plus.
 */
function ContenuValide({ bon, valide, config }: { bon: BonDetail; valide: BonValide; config: ConfigModele }) {
  const lignes: LigneALire[] = valide.lines.map((l) => ({
    cle: l.id,
    kind: l.kind,
    reference: l.reference,
    designation: l.designation,
    quantity: l.quantity,
    unit: l.unit,
    serialNumber: l.serialNumber,
    brouillon: false,
  }));
  const machineId = bon.asset?.id ?? null;

  const rubriques: Record<string, ReactNode> = {
    probleme: <CarteTexte key="probleme" titre="Problème signalé" texte={bon.details.reportedProblem} exige />,
    travaux: <CarteTexte key="travaux" titre="Travaux réalisés" texte={bon.details.workDone} exige />,
    controles: <ControlesDuBon key="controles" controles={valide.checks} titre={config.titreControles} />,
    valeurs: config.releves && (
      <div className="carte" key="valeurs">
        <h2>{config.titreValeurs}</h2>
        <div className="carte-corps">
          {valide.readings.length === 0 ? (
            <p className="aide">Aucun relevé de compteur dans ce bon.</p>
          ) : (
            <table className="tableau-bon">
              <thead>
                <tr>
                  <th>Compteur</th>
                  <th>Valeur</th>
                </tr>
              </thead>
              <tbody>
                {valide.readings.map((r) => (
                  <tr key={r.id}>
                    <td>{r.counterLabel}</td>
                    <td>
                      {valeurAffichee(r.value)} <span className="aide">{r.unit ?? ''}</span>
                      {r.adopte && (
                        <div className="aide">
                          Remplace un relevé identique déjà saisi : l’ancien reste dans l’historique du compteur, marqué « remplacé ».
                        </div>
                      )}
                      {r.anomaly && r.anomalyReason && <div className="motif-anomalie">{r.anomalyReason}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {valide.service && (
            <p className="aide">
              Entretien réalisé enregistré : palier <strong>{valide.service.levelCode}</strong> le {jourFr(valide.service.performedOn)}
              {valide.service.counterValue ? ` à ${valeurAffichee(valide.service.counterValue)} h` : ''}
              {valide.service.adopte ? ' (il remplace l’entretien identique déjà saisi, qui reste dans l’historique)' : ''}.{' '}
              {machineId && <Link to={`/parc?asset=${machineId}`}>Voir la fiche de la machine</Link>}
            </p>
          )}
        </div>
      </div>
    ),
    dossier: <CarteDossier key="dossier" dossier={bon.details.dossier} />,
    periode: <CartePeriode key="periode" du={bon.details.rentalFrom} au={bon.details.rentalReturn} />,
    lignes: <CarteLignes key="lignes" lignes={lignes} config={config} />,
    reserves: <CarteTexte key="reserves" titre="Réserves du client" texte={bon.details.reservations} />,
    suites: config.suites && valide.followUps.length > 0 && (
      <div className="carte" key="suites">
        <h2>Suites à donner</h2>
        <div className="carte-corps">
          <ul className="suites-bon">
            {valide.followUps.map((s) => (
              <li key={s.id}>
                <strong>{LIBELLE_SUITE[s.kind]}</strong> — {s.comment}{' '}
                <span className="aide">({LIBELLE_ETAT_SUITE[s.status]})</span>
                {s.resolutionNote && <div className="aide">{s.resolutionNote}</div>}
              </li>
            ))}
          </ul>
          <p className="aide">
            Elles restent ouvertes tant qu’on ne les a pas traitées : on les écarte ou on les clôt depuis{' '}
            <Link to={machineId ? `/suites?machine=${machineId}` : '/suites'}>Suites à traiter</Link>
            {machineId ? ', et elles se lisent aussi sur la fiche de la machine' : ''}.
          </p>
        </div>
      </div>
    ),
    observations: bon.observations && (
      <div className="carte" key="observations">
        <h2>Observations</h2>
        <div className="carte-corps observations-bon">{bon.observations}</div>
      </div>
    ),
  };

  return <>{config.ordre.map((r) => rubriques[r] ?? null)}</>;
}

/** « gamme B » pour une ligne versée depuis la gamme, « en plus » pour une pièce, une main d'œuvre ou un déplacement ajoutés à la main. */
function marqueDeLigne(l: ContenuBon['lines'][number]): LigneALire['marque'] | null {
  if (l.versedFrom) return { texte: `gamme ${l.versedFrom.levelCode}`, classe: 'pastille-gamme' };
  if (estEnPlus(l.kind)) return { texte: 'en plus', classe: 'pastille-en-plus' };
  return null;
}

/** Le palier unique des lignes versées (« B ») ; `null` sinon. */
function palierDesLignes(lignes: ContenuBon['lines']): string | null {
  const paliers = new Set(lignes.flatMap((l) => (l.versedFrom ? [l.versedFrom.levelCode] : [])));
  return paliers.size === 1 ? [...paliers][0]! : null;
}

/** Les heures de l'équipe, par jour et par technicien ; facturées au quart d'heure une fois le bon validé. */
function CarteHeures({ heures }: { heures: BonDetail['planification']['heures'] }) {
  return (
    <div className="carte">
      <h2>Heures de l’équipe</h2>
      <div className="carte-corps">
        <table className="tableau-bon tableau-heures-bon">
          <thead>
            <tr>
              <th>Jour</th>
              <th>Technicien</th>
              <th>Heures</th>
              <th className="montant">Facturé au quart d’heure</th>
            </tr>
          </thead>
          <tbody>
            {heures.map((h) => (
              <tr key={`${h.jour}-${h.technicien}`}>
                <td>{jourFr(h.jour)}</td>
                <td>{h.technicien}</td>
                <td>{dureeFr(h.minutes)}</td>
                <td className="montant">{h.minutesFacturees !== null ? dureeFr(h.minutesFacturees) : <span className="aide">{quartDHeure(h.minutes)} (à la validation)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * La clôture faite sur le téléphone (lot T3) : « Signé par … le … » ou « Client absent : motif », et les deux images de signature
 * (lues par leur lien de lecture à jeton ; une image illisible n’empêche pas la phrase).
 */
export function CarteCloture({ cloture }: { cloture: ClotureBon }) {
  const [liens, setLiens] = useState<{ client: string | null; technicien: string | null }>({ client: null, technicien: null });
  useEffect(() => {
    let annule = false;
    const lire = (id: string | null): Promise<string | null> =>
      id === null ? Promise.resolve(null) : api.fileUrl(id).then((u) => u.url).catch(() => null);
    void Promise.all([lire(cloture.clientSignatureFileId), lire(cloture.technicianSignatureFileId)]).then(([client, technicien]) => {
      if (!annule) setLiens({ client, technicien });
    });
    return () => {
      annule = true;
    };
  }, [cloture.clientSignatureFileId, cloture.technicianSignatureFileId]);
  return (
    <div className="carte">
      <h2>Clôture sur le téléphone</h2>
      <div className="carte-corps">
        <p className={cloture.absentReason !== null ? 'recap-attention' : undefined}>{phraseCloture(cloture)}</p>
        <div className="signatures-bon">
          <figure>
            <figcaption>Signature du client</figcaption>
            {liens.client ? <img src={liens.client} alt="Signature du client" /> : <span className="aide">{cloture.absentReason !== null ? 'Client absent' : '—'}</span>}
          </figure>
          <figure>
            <figcaption>Signature du technicien (pour l’équipe)</figcaption>
            {liens.technicien ? <img src={liens.technicien} alt="Signature du technicien" /> : <span className="aide">—</span>}
          </figure>
        </div>
      </div>
    </div>
  );
}

export function DetailBon({ bon, onChange }: { bon: BonDetail; onChange?: () => void }) {
  const droits = useDroitsBons();
  const droitsDuPlanning = useDroitsPlanning();
  const [compteurs, setCompteurs] = useState<Compteur[] | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceMachine | null>(null);
  const machineId = bon.asset?.id ?? null;
  const modele = bon.nature.layout;
  const config = configDe(modele);

  // Les libellés des compteurs et le code du palier : lus sur la machine, sans bloquer l'écran s'ils manquent.
  // Une livraison sur un site seul n'a rien à lire.
  useEffect(() => {
    let annule = false;
    setCompteurs(null);
    setMaintenance(null);
    if (machineId === null) {
      setCompteurs([]);
      return;
    }
    api
      .counters(machineId)
      .then((c) => {
        if (!annule) setCompteurs(c);
      })
      .catch(() => {
        if (!annule) setCompteurs([]);
      });
    api
      .assetMaintenance(machineId)
      .then((g) => {
        if (!annule) setMaintenance(g);
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, [machineId]);

  const contenu = bon.content;
  const libelleCompteur = (id: string): string => compteurs?.find((c) => c.id === id)?.label ?? 'Compteur archivé ou inconnu';
  const uniteCompteur = (id: string): string => compteurs?.find((c) => c.id === id)?.unit ?? '';
  const palier = contenu?.levelKey ? paliersDeLaGamme(maintenance).find((p) => p.levelKey === contenu.levelKey) : undefined;
  const mentionPrx = mentionPraxedo(bon);
  const nbTechniciens = bon.validated?.technicians.length ?? bon.content?.technicians.length ?? ((bon.technicianLabel ?? '').includes(', ') ? 2 : 1);

  const lignesBrouillon: LigneALire[] = (contenu?.lines ?? []).map((l) => ({
    cle: l.lineKey,
    kind: l.kind,
    reference: l.reference,
    designation: l.designation,
    quantity: l.quantity,
    unit: l.unit,
    serialNumber: l.serialNumber,
    brouillon: true,
    ...(marqueDeLigne(l) ? { marque: marqueDeLigne(l)! } : {}),
  }));

  const rubriquesBrouillon: Record<string, ReactNode> = contenu
    ? {
        probleme: <CarteTexte key="probleme" titre="Problème signalé" texte={contenu.details.reportedProblem} exige />,
        travaux: <CarteTexte key="travaux" titre="Travaux réalisés" texte={contenu.details.workDone} exige />,
        controles: <ControlesDuBon key="controles" controles={contenu.checks} titre={config.titreControles} />,
        valeurs: config.releves && (
          <div className="carte" key="valeurs">
            <h2>{config.titreValeurs}</h2>
            <div className="carte-corps">
              {contenu.readings.length === 0 ? (
                <p className="aide">Aucun relevé de compteur dans ce bon.</p>
              ) : (
                <table className="tableau-bon">
                  <thead>
                    <tr>
                      <th>Compteur</th>
                      <th>Valeur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contenu.readings.map((r) => (
                      <tr key={r.counterId}>
                        <td>{libelleCompteur(r.counterId)}</td>
                        <td>
                          {valeurAffichee(r.value) || <span className="aide">non saisie</span>}{' '}
                          <span className="aide">{uniteCompteur(r.counterId)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {contenu.levelKey && (
                <p className="aide">
                  Palier réalisé :{' '}
                  <strong>{palier ? `${palier.code} — ${palier.label}` : 'un palier de la gamme (la gamme a changé depuis)'}</strong>
                </p>
              )}
            </div>
          </div>
        ),
        dossier: <CarteDossier key="dossier" dossier={contenu.details.dossier} />,
        periode: <CartePeriode key="periode" du={contenu.details.rentalFrom} au={contenu.details.rentalReturn} />,
        lignes: <CarteLignes key="lignes" lignes={lignesBrouillon} config={config} />,
        reserves: <CarteTexte key="reserves" titre="Réserves du client" texte={contenu.details.reservations} />,
        suites: config.suites && contenu.followUps.length > 0 && (
          <div className="carte" key="suites">
            <h2>Suites à donner</h2>
            <div className="carte-corps">
              <ul className="suites-bon">
                {contenu.followUps.map((s) => (
                  <li key={s.followUpKey}>
                    <strong>{LIBELLE_SUITE[s.kind]}</strong>
                    {s.comment ? ` — ${s.comment}` : ' — commentaire à saisir'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ),
        observations: bon.observations && (
          <div className="carte" key="observations">
            <h2>Observations</h2>
            <div className="carte-corps observations-bon">{bon.observations}</div>
          </div>
        ),
      }
    : {};

  return (
    <>
      <div className="carte">
        <div className="carte-corps">
          <dl className="proprietes">
            <Propriete libelle="Machine">
              {bon.asset ? (
                <>
                  <Link to={`/parc?asset=${bon.asset.id}`}>{libelleMachine(bon.asset)}</Link>
                  {bon.asset.archived && <span className="etat revoque"> archivée</span>}
                </>
              ) : (
                <span className="aide">Aucune : livraison faite sur le site</span>
              )}
            </Propriete>
            <Propriete libelle="Client">{bon.customer?.name}</Propriete>
            <Propriete libelle="Site">
              {bon.site ? <Link to={`/sites/${bon.site.id}`}>{bon.site.name}</Link> : null}
            </Propriete>
            <Propriete libelle="Facturation">
              {bon.billing ? (
                <span className={`pastille-facturation ${classeFacturation(bon.billing)}`}>{LIBELLE_FACTURATION[bon.billing]}</span>
              ) : (
                <span className="aide">Pas encore choisie</span>
              )}
            </Propriete>
            <Propriete libelle="Date de l’intervention">{bon.performedOn ? jourFr(bon.performedOn) : null}</Propriete>
            <Propriete libelle={libelleTechniciens(modele, nbTechniciens)}>{bon.technicianLabel}</Propriete>
            <Propriete libelle="Contact sur place">{bon.contactLabel}</Propriete>
            {mentionPrx && <Propriete libelle="Bon repris de Praxedo">{bon.praxedoNumber}</Propriete>}
          </dl>
        </div>
      </div>

      {bon.planification.heures.length > 0 && <CarteHeures heures={bon.planification.heures} />}

      {/* Registre B34 : les pointages (on ajoute ici un oubli seulement pour qui a déjà pointé ce jour-là ; le planning les propose tous). */}
      {bon.pointages.length > 0 && (
        <CartePointages
          interventionId={bon.id}
          pointages={bon.pointages}
          membres={bon.pointages.map((p) => ({ jour: p.jour, technicienId: p.technicien.id, nom: p.technicien.nom }))}
          peutGerer={droitsDuPlanning.peutGererPlanning && bon.status !== 'REVIEWED' && bon.status !== 'CANCELLED'}
          onChange={onChange ?? (() => undefined)}
        />
      )}

      {bon.validated && <ContenuValide bon={bon} valide={bon.validated} config={config} />}

      {contenu === null && bon.validated === null && (
        <div className="message info">Ce bon n’a pas de contenu à afficher.</div>
      )}

      {contenu && <>{config.ordre.map((r) => rubriquesBrouillon[r] ?? null)}</>}

      {/* Le chiffrage du bon (ébauche du 2026-10-24) : compris au contrat ou à facturer ; figé une fois le bon validé. */}
      {droits.peutVoirChiffrage && bon.status !== 'CANCELLED' && (
        <ChiffrageBon interventionId={bon.id} cle={bon.revision} palier={palierDesLignes(contenu?.lines ?? [])} />
      )}

      {bon.cloture && <CarteCloture cloture={bon.cloture} />}

      <PiecesJointes ownerType="intervention" ownerId={bon.id} />
    </>
  );
}
